<template>
  <div class="flex flex-row gap-y-1" v-show="isShow">
    <div
      :class="direction"
      class="w-full flex !items-center gap-x-4 gap-y-1 text-brand-black-secondary"
    >
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="relative flex items-center gap-x-2"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full relative flex justify-start items-start gap-x-2">
        <div
          :key="key"
          v-for="(optionItem, key) in props.sequenceOptions"
          class="cursor-pointer flex items-start gap-x-2"
        >
          <TextInput
            :key="optionItem.field_name"
            :field-name="optionItem.field_name"
            :dependent-on="optionItem.dependent_on"
            :on-blur="optionItem.on_blur"
            :default-value="
              formStore.draft?.request?.[optionItem.field_name] || optionItem.default_value
            "
            :is-disabled="optionItem.disabled || props.isDisabled"
            :date-format="optionItem.date_format"
            :min-date="optionItem.min_date"
            :max-date="optionItem.max_date"
            :prefixDisabled="optionItem.prefixDisabled"
            :showRules="optionItem.showRules"
            :radioOptions="optionItem.radio_options"
            :full-width="false"
            :input-width="calculateInputWidth(optionItem)"
            v-bind="{ ...optionItem, ...layoutClasses(props?.layout) }"
          />
          <div class="text-gray-400 mt-2" v-show="key != props.sequenceOptions.length - 1">/</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, toRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormStore } from '@/stores/form'
import useVisible from '@/composables/useVisible'
import { useRoute } from 'vue-router'
import TextInput from '@/components/Form/TextInput.vue'
import HintTippy from '@/components/Form/HintTyppy.vue'

const props = defineProps({
  layout: {
    type: String,
    default: 'one-column'
  },
  hint: String,
  labelValue: String,
  radioOptions: {
    type: Array,
    required: true
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: [String, Number]
  },
  rules: Array,
  showRules: Object,
  defaultValue: String,
  direction: {
    type: String,
    default: 'flex-row md:items-start '
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String,
  sequenceOptions: Array,
  debounceTime: {
    type: Number,
    default: () => 0
  },
  isDisabled: {
    type: [Boolean, Object]
  }
})

const formStore = useFormStore()

const i18n = useI18n()
const { findShowRule, getBindObj } = useVisible()
const route = useRoute()

const isShow = computed(() => {
  if (props.showRules) {
    return findShowRule(
      props.showRules.any || props.showRules.all,
      props.showRules.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
  } else {
    return true
  }
})
const t9nLabel = computed(() => i18n.t(props.label))

const layoutClasses = (layout) => {
  if (layout === 'two-column') {
    return {
      bodyClass: 'flex flex-wrap gap-y-2',
      class: 'px-1',
      direction: 'flex-col',
      labelClass: '!w-full',
      showErrorSpacer: false
    }
  } else {
    return {
      bodyClass: 'flex flex-col gap-y-2',
      showErrorSpacer: false
    }
  }
}

const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))

const inputWidthMultiplier = 5
const inputWidthMax = 16

const calculateInputWidth = (option) => {
  const maxLenRule = option.rules.find((r) => r.name == 'max_char')
  if (!maxLenRule) return null

  const { value } = maxLenRule

  /**
   * Calculate the product of the max character
   * value and the input width multiplier
   *
   * then determine the lesser value between the calculated product
   * and the maximum input width so we wont have 50ch
   */
  const product = value * inputWidthMultiplier
  const lesser = Math.min(product, inputWidthMax)

  // Return the lesser value appended with 'ch' (character unit)
  return `${lesser}ch`
}
</script>

<style lang="scss" scoped></style>
